import React, { useState } from 'react';

interface HealthCheckinModalProps {
  isOpen: boolean;
  onClose: () => void;
  experimentTitle: string;
  experimentType: 'nutrition' | 'sleep';
  checkinPrompts: HealthCheckinPrompt[];
  onSubmit: (responses: HealthCheckinResponse[]) => void;
}

interface HealthCheckinPrompt {
  id: string;
  question: string;
  type: 'scale' | 'tags' | 'text' | 'boolean' | 'time';
  options?: string[];
  min?: number;
  max?: number;
  placeholder?: string;
}

interface HealthCheckinResponse {
  promptId: string;
  value: any;
  timestamp: string;
}

export const HealthCheckinModal: React.FC<HealthCheckinModalProps> = ({
  isOpen,
  onClose,
  experimentTitle,
  experimentType,
  checkinPrompts,
  onSubmit
}) => {
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [responses, setResponses] = useState<HealthCheckinResponse[]>([]);
  const [currentResponse, setCurrentResponse] = useState<any>(null);

  if (!isOpen) return null;

  const currentPrompt = checkinPrompts[currentPromptIndex];
  const isLastPrompt = currentPromptIndex === checkinPrompts.length - 1;
  const progress = ((currentPromptIndex + 1) / checkinPrompts.length) * 100;

  const handleNext = () => {
    if (currentResponse !== null && currentResponse !== undefined) {
      const newResponse: HealthCheckinResponse = {
        promptId: currentPrompt.id,
        value: currentResponse,
        timestamp: new Date().toISOString()
      };

      const updatedResponses = [...responses, newResponse];
      setResponses(updatedResponses);

      if (isLastPrompt) {
        onSubmit(updatedResponses);
        handleClose();
      } else {
        setCurrentPromptIndex(currentPromptIndex + 1);
        setCurrentResponse(null);
      }
    }
  };

  const handleBack = () => {
    if (currentPromptIndex > 0) {
      setCurrentPromptIndex(currentPromptIndex - 1);
      // Restore previous response
      const previousResponse = responses[currentPromptIndex - 1];
      setCurrentResponse(previousResponse?.value || null);
      // Remove the last response from array
      setResponses(responses.slice(0, -1));
    }
  };

  const handleClose = () => {
    setCurrentPromptIndex(0);
    setResponses([]);
    setCurrentResponse(null);
    onClose();
  };

  const renderPromptInput = () => {
    switch (currentPrompt.type) {
      case 'scale':
        return (
          <div className="space-y-4">
            <div className="flex justify-between text-sm text-gray-500">
              <span>{currentPrompt.min || 1}</span>
              <span>{currentPrompt.max || 5}</span>
            </div>
            <div className="flex justify-between items-center">
              {Array.from({ length: (currentPrompt.max || 5) - (currentPrompt.min || 1) + 1 }, (_, i) => {
                const value = (currentPrompt.min || 1) + i;
                return (
                  <button
                    key={value}
                    onClick={() => setCurrentResponse(value)}
                    className={`w-12 h-12 rounded-full border-2 font-semibold transition-all ${
                      currentResponse === value
                        ? experimentType === 'nutrition'
                          ? 'bg-green-600 border-green-600 text-white'
                          : 'bg-blue-600 border-blue-600 text-white'
                        : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
            {currentPrompt.options && (
              <div className="flex justify-between text-xs text-gray-500">
                <span>{currentPrompt.options[0]}</span>
                <span>{currentPrompt.options[1]}</span>
              </div>
            )}
          </div>
        );

      case 'tags':
        return (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {currentPrompt.options?.map((option) => (
                <button
                  key={option}
                  onClick={() => {
                    const currentTags = Array.isArray(currentResponse) ? currentResponse : [];
                    const isSelected = currentTags.includes(option);
                    const newTags = isSelected
                      ? currentTags.filter((tag: string) => tag !== option)
                      : [...currentTags, option];
                    setCurrentResponse(newTags);
                  }}
                  className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                    Array.isArray(currentResponse) && currentResponse.includes(option)
                      ? experimentType === 'nutrition'
                        ? 'bg-green-600 text-white'
                        : 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500">Select all that apply</p>
          </div>
        );

      case 'boolean':
        return (
          <div className="flex gap-3">
            <button
              onClick={() => setCurrentResponse(true)}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                currentResponse === true
                  ? experimentType === 'nutrition'
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Yes
            </button>
            <button
              onClick={() => setCurrentResponse(false)}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                currentResponse === false
                  ? experimentType === 'nutrition'
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              No
            </button>
          </div>
        );

      case 'text':
        return (
          <textarea
            value={currentResponse || ''}
            onChange={(e) => setCurrentResponse(e.target.value)}
            placeholder={currentPrompt.placeholder || 'Type your response...'}
            className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={3}
          />
        );

      case 'time':
        return (
          <div className="space-y-3">
            <input
              type="time"
              value={currentResponse || ''}
              onChange={(e) => setCurrentResponse(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    if (currentPrompt.type === 'tags') {
      return Array.isArray(currentResponse) && currentResponse.length > 0;
    }
    return currentResponse !== null && currentResponse !== undefined && currentResponse !== '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{experimentType === 'nutrition' ? '🧪' : '🌙'}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                experimentType === 'nutrition' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                Daily Check-in
              </span>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <h2 className="text-lg font-semibold text-gray-900 mb-2">{experimentTitle}</h2>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                experimentType === 'nutrition' ? 'bg-green-600' : 'bg-blue-600'
              }`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Question {currentPromptIndex + 1} of {checkinPrompts.length}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {currentPrompt.question}
            </h3>
            {renderPromptInput()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex gap-3">
            {currentPromptIndex > 0 && (
              <button
                onClick={handleBack}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                canProceed()
                  ? experimentType === 'nutrition'
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isLastPrompt ? 'Complete Check-in' : 'Next'}
            </button>
          </div>
        </div>

        {/* Quick Tips */}
        <div className="px-6 pb-6">
          <div className={`rounded-lg p-3 ${
            experimentType === 'nutrition' ? 'bg-green-50' : 'bg-blue-50'
          }`}>
            <p className={`text-xs ${
              experimentType === 'nutrition' ? 'text-green-700' : 'text-blue-700'
            }`}>
              💡 <strong>Tip:</strong> Be honest with your responses. The more accurate your data, the better insights you'll discover about your body.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 